import { useEffect, useState } from 'react';
import { Landmark } from 'lucide-react';
import apiService from '../../services/api';
import { useToast } from '../../hooks/useToast';
import Toast from '../Toast';
import {
  SettingsCard,
  SettingsTextField,
} from './settingsUi.jsx';

/**
 * Coordonnées bancaires NR2J — Super Admin uniquement.
 * Injectées automatiquement dans devis + factures PDF (pas contrats).
 */
export default function Nr2jBankSettings() {
  const { toasts, toast, removeToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    bankAccountHolder: '',
    bankIban: '',
    bankBic: '',
    bankName: '',
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiService.getNr2jBankSettings();
        const bank = res?.bank || res || {};
        if (cancelled) return;
        setForm({
          bankAccountHolder: bank.bankAccountHolder || '',
          bankIban: bank.bankIban || '',
          bankBic: bank.bankBic || '',
          bankName: bank.bankName || '',
        });
      } catch (err) {
        if (!cancelled) {
          toast.error(err?.message || 'Impossible de charger le RIB NR2J');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [toast]);

  const onChange = (key) => (value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await apiService.updateNr2jBankSettings(form);
      const bank = res?.bank || res || {};
      setForm({
        bankAccountHolder: bank.bankAccountHolder || '',
        bankIban: bank.bankIban || '',
        bankBic: bank.bankBic || '',
        bankName: bank.bankName || '',
      });
      toast.success('Coordonnées bancaires enregistrées');
    } catch (err) {
      toast.error(err?.message || 'Échec de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SettingsCard
        icon={Landmark}
        title="Coordonnées bancaires"
        subtitle="RIB NR2J unique — injecté automatiquement dans les devis et factures PDF. Jamais dans les contrats."
      >
        {loading ? (
          <p className="text-sm text-gray-500">Chargement…</p>
        ) : (
          <form onSubmit={save} className="space-y-4" data-testid="nr2j-bank-settings-form">
            <SettingsTextField
              label="Titulaire"
              value={form.bankAccountHolder}
              onChange={onChange('bankAccountHolder')}
              autoComplete="off"
            />
            <SettingsTextField
              label="IBAN"
              value={form.bankIban}
              onChange={onChange('bankIban')}
              autoComplete="off"
            />
            <SettingsTextField
              label="BIC"
              value={form.bankBic}
              onChange={onChange('bankBic')}
              autoComplete="off"
            />
            <SettingsTextField
              label="Banque"
              value={form.bankName}
              onChange={onChange('bankName')}
              autoComplete="off"
            />
            <div className="flex justify-end pt-1">
              <button
                type="submit"
                disabled={saving}
                className="inline-flex items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-60"
                data-testid="nr2j-bank-settings-save"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </form>
        )}
      </SettingsCard>
      {toasts.map((item) => (
        <Toast key={item.id} toast={item} onClose={() => removeToast(item.id)} />
      ))}
    </>
  );
}
